import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, Plus, DollarSign, TrendingUp, TrendingDown, Receipt,
  FileText, Calendar, AlertCircle, CheckCircle2, Clock, Trash2, Pencil,
  ArrowUpCircle, ArrowDownCircle, Wallet, BarChart3
} from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { format, startOfMonth, endOfMonth, subMonths, isAfter, isBefore, parseISO, addMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { motion } from "framer-motion";

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;
const tooltipStyle = { contentStyle: { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 } };
const COLORS = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

type FinCategory = { id: string; name: string; type: string; color: string; is_system: boolean; restaurant_id: string };
type FinTransaction = { id: string; restaurant_id: string; category_id: string | null; type: string; description: string; amount: number; date: string; is_recurring: boolean; recurrence_interval: string | null; notes: string | null };
type Bill = { id: string; restaurant_id: string; category_id: string | null; type: string; description: string; amount: number; due_date: string; paid_date: string | null; status: string; notes: string | null };

const Financeiro = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  // Data
  const [categories, setCategories] = useState<FinCategory[]>([]);
  const [transactions, setTransactions] = useState<FinTransaction[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [orders, setOrders] = useState<{ total: number; created_at: string; status: string }[]>([]);

  // Filters
  const [period, setPeriod] = useState("current"); // current | last | 3months
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<"transaction" | "bill" | "category">("transaction");

  // Form state
  const [form, setForm] = useState<any>({});

  const periodRange = useMemo(() => {
    const now = new Date();
    if (period === "current") return { start: startOfMonth(now), end: endOfMonth(now) };
    if (period === "last") return { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)) };
    return { start: startOfMonth(subMonths(now, 2)), end: endOfMonth(now) };
  }, [period]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { navigate("/login"); return; }

    const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
    let rest: any = null;
    
    if (impersonateId) {
      const { data } = await supabase
        .from("restaurants").select("id").eq("id", impersonateId).single();
      rest = data;
    } else {
      const { data } = await supabase
        .from("restaurants").select("id").eq("owner_id", user.id).single();
      rest = data;
    }
    
    if (!rest) { navigate("/dashboard"); return; }
    setRestaurantId(rest.id);

    const [catRes, txRes, billRes, ordRes] = await Promise.all([
      supabase.from("financial_categories").select("*").eq("restaurant_id", rest.id),
      supabase.from("financial_transactions").select("*").eq("restaurant_id", rest.id).order("date", { ascending: false }),
      supabase.from("bills").select("*").eq("restaurant_id", rest.id).order("due_date", { ascending: true }),
      supabase.from("orders").select("total, created_at, status").eq("restaurant_id", rest.id),
    ]);

    setCategories((catRes.data as any[]) || []);
    setTransactions((txRes.data as any[]) || []);
    setBills((billRes.data as any[]) || []);
    setOrders(ordRes.data || []);
    setLoading(false);
  };

  // Computed metrics
  const completedOrders = useMemo(() => orders.filter(o => o.status === "completed"), [orders]);

  const periodOrders = useMemo(() =>
    completedOrders.filter(o => {
      const d = parseISO(o.created_at);
      return !isBefore(d, periodRange.start) && !isAfter(d, periodRange.end);
    }),
    [completedOrders, periodRange]
  );

  const periodTransactions = useMemo(() =>
    transactions.filter(t => {
      const d = parseISO(t.date);
      return !isBefore(d, periodRange.start) && !isAfter(d, periodRange.end);
    }),
    [transactions, periodRange]
  );

  const totalRevenue = useMemo(() => periodOrders.reduce((s, o) => s + Number(o.total), 0) + periodTransactions.filter(t => t.type === "revenue").reduce((s, t) => s + Number(t.amount), 0), [periodOrders, periodTransactions]);
  const totalExpenses = useMemo(() => periodTransactions.filter(t => t.type === "expense").reduce((s, t) => s + Number(t.amount), 0), [periodTransactions]);
  const netProfit = totalRevenue - totalExpenses;

  const pendingBills = useMemo(() => bills.filter(b => b.status === "pending"), [bills]);
  const overdueBills = useMemo(() => bills.filter(b => b.status === "pending" && isBefore(parseISO(b.due_date), new Date())), [bills]);

  // Cash flow data for chart
  const cashFlowData = useMemo(() => {
    const months: { month: string; receitas: number; despesas: number; lucro: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const m = subMonths(new Date(), i);
      const start = startOfMonth(m);
      const end = endOfMonth(m);
      const rev = completedOrders.filter(o => { const d = parseISO(o.created_at); return !isBefore(d, start) && !isAfter(d, end); }).reduce((s, o) => s + Number(o.total), 0)
        + transactions.filter(t => t.type === "revenue" && (() => { const d = parseISO(t.date); return !isBefore(d, start) && !isAfter(d, end); })()).reduce((s, t) => s + Number(t.amount), 0);
      const exp = transactions.filter(t => t.type === "expense" && (() => { const d = parseISO(t.date); return !isBefore(d, start) && !isAfter(d, end); })()).reduce((s, t) => s + Number(t.amount), 0);
      months.push({ month: format(m, "MMM", { locale: ptBR }), receitas: rev, despesas: exp, lucro: rev - exp });
    }
    return months;
  }, [completedOrders, transactions]);

  // Expenses by category for pie chart
  const expensesByCategory = useMemo(() => {
    const map: Record<string, number> = {};
    periodTransactions.filter(t => t.type === "expense").forEach(t => {
      const cat = categories.find(c => c.id === t.category_id);
      const name = cat?.name || "Sem categoria";
      map[name] = (map[name] || 0) + Number(t.amount);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [periodTransactions, categories]);

  // CRUD handlers
  const openDialog = (type: "transaction" | "bill" | "category", editItem?: any) => {
    setDialogType(type);
    if (editItem) {
      setForm({ ...editItem, category_id: editItem.category_id || "none", editing: true });
    } else {
      if (type === "transaction") setForm({ type: "expense", description: "", amount: "", date: format(new Date(), "yyyy-MM-dd"), category_id: "none", is_recurring: false, recurrence_interval: "", notes: "" });
      else if (type === "bill") setForm({ type: "payable", description: "", amount: "", due_date: format(new Date(), "yyyy-MM-dd"), category_id: "none", status: "pending", notes: "" });
      else setForm({ name: "", type: "expense", color: "#6366f1" });
    }
    setDialogOpen(true);
  };

  const saveItem = async () => {
    if (!restaurantId) return;
    try {
      if (dialogType === "category") {
        const payload = { restaurant_id: restaurantId, name: form.name, type: form.type, color: form.color };
        if (form.editing) {
          await supabase.from("financial_categories").update(payload).eq("id", form.id);
        } else {
          await supabase.from("financial_categories").insert(payload);
        }
      } else if (dialogType === "transaction") {
        const catId = form.category_id === "none" ? null : form.category_id || null;
        const payload = { restaurant_id: restaurantId, type: form.type, description: form.description, amount: parseFloat(form.amount), date: form.date, category_id: catId, is_recurring: form.is_recurring, recurrence_interval: form.is_recurring ? form.recurrence_interval : null, notes: form.notes || null };
        if (form.editing) {
          await supabase.from("financial_transactions").update(payload).eq("id", form.id);
        } else {
          await supabase.from("financial_transactions").insert(payload);
        }
      } else {
        const catId = form.category_id === "none" ? null : form.category_id || null;
        const payload = { restaurant_id: restaurantId, type: form.type, description: form.description, amount: parseFloat(form.amount), due_date: form.due_date, category_id: catId, status: form.status || "pending", notes: form.notes || null, paid_date: form.status === "paid" ? format(new Date(), "yyyy-MM-dd") : null };
        if (form.editing) {
          await supabase.from("bills").update(payload).eq("id", form.id);
        } else {
          await supabase.from("bills").insert(payload);
        }
      }
      toast({ title: "Salvo com sucesso!" });
      setDialogOpen(false);
      loadData();
    } catch {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
  };

  const deleteItem = async (table: string, id: string) => {
    await supabase.from(table as any).delete().eq("id", id);
    toast({ title: "Removido!" });
    loadData();
  };

  const markBillPaid = async (id: string) => {
    await supabase.from("bills").update({ status: "paid", paid_date: format(new Date(), "yyyy-MM-dd") } as any).eq("id", id);
    toast({ title: "Conta marcada como paga!" });
    loadData();
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground">Carregando...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-card/80 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-bold">Gestão Financeira</h1>
        </div>
        <div className="ml-auto flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="current">Mês atual</SelectItem>
              <SelectItem value="last">Mês anterior</SelectItem>
              <SelectItem value="3months">Últimos 3 meses</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-4">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard icon={TrendingUp} label="Receita" value={formatCurrency(totalRevenue)} color="text-emerald-500" />
          <KpiCard icon={TrendingDown} label="Despesas" value={formatCurrency(totalExpenses)} color="text-red-500" />
          <KpiCard icon={DollarSign} label="Lucro Líquido" value={formatCurrency(netProfit)} color={netProfit >= 0 ? "text-emerald-500" : "text-red-500"} />
          <KpiCard icon={AlertCircle} label="Contas Vencidas" value={String(overdueBills.length)} color={overdueBills.length > 0 ? "text-red-500" : "text-muted-foreground"} />
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="overview" className="text-xs sm:text-sm">DRE</TabsTrigger>
            <TabsTrigger value="cashflow" className="text-xs sm:text-sm">Fluxo Caixa</TabsTrigger>
            <TabsTrigger value="bills" className="text-xs sm:text-sm">Contas</TabsTrigger>
            <TabsTrigger value="expenses" className="text-xs sm:text-sm">Despesas</TabsTrigger>
          </TabsList>

          {/* DRE Tab */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> DRE Simplificado</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <DreRow label="Receita de Vendas (Pedidos)" value={periodOrders.reduce((s, o) => s + Number(o.total), 0)} positive />
                <DreRow label="Outras Receitas" value={periodTransactions.filter(t => t.type === "revenue").reduce((s, t) => s + Number(t.amount), 0)} positive />
                <div className="border-t border-border pt-2">
                  <DreRow label="Total Receitas" value={totalRevenue} positive bold />
                </div>
                <div className="border-t border-border pt-2">
                  {expensesByCategory.map(ec => (
                    <DreRow key={ec.name} label={`(-) ${ec.name}`} value={ec.value} />
                  ))}
                  {expensesByCategory.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma despesa registrada</p>}
                </div>
                <div className="border-t border-border pt-2">
                  <DreRow label="Total Despesas" value={totalExpenses} bold />
                </div>
                <div className="border-t-2 border-primary/30 pt-3">
                  <DreRow label="Resultado Líquido" value={netProfit} positive={netProfit >= 0} bold highlight />
                </div>
              </CardContent>
            </Card>

            {/* Expense pie */}
            {expensesByCategory.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Despesas por Categoria</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={expensesByCategory} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                          {expensesByCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v: number) => formatCurrency(v)} {...tooltipStyle} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Cash Flow Tab */}
          <TabsContent value="cashflow" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Fluxo de Caixa - Últimos 6 meses</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={cashFlowData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v: number) => formatCurrency(v)} {...tooltipStyle} />
                      <Bar dataKey="receitas" name="Receitas" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="despesas" name="Despesas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Evolução do Lucro</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={cashFlowData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v: number) => formatCurrency(v)} {...tooltipStyle} />
                      <Area type="monotone" dataKey="lucro" name="Lucro" stroke="#6366f1" fill="#6366f1" fillOpacity={0.15} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bills Tab */}
          <TabsContent value="bills" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold">Contas a Pagar / Receber</h2>
              <Button size="sm" onClick={() => openDialog("bill")}><Plus className="w-4 h-4 mr-1" /> Nova Conta</Button>
            </div>

            {overdueBills.length > 0 && (
              <Card className="border-destructive/50 bg-destructive/5">
                <CardContent className="p-3">
                  <p className="text-sm font-medium text-destructive flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {overdueBills.length} conta(s) vencida(s) — total: {formatCurrency(overdueBills.reduce((s, b) => s + Number(b.amount), 0))}</p>
                </CardContent>
              </Card>
            )}

            <div className="space-y-2">
              {bills.map(bill => {
                const isOverdue = bill.status === "pending" && isBefore(parseISO(bill.due_date), new Date());
                const catName = categories.find(c => c.id === bill.category_id)?.name;
                return (
                  <motion.div key={bill.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${bill.type === "payable" ? "bg-red-500/10 text-red-500" : "bg-emerald-500/10 text-emerald-500"}`}>
                      {bill.type === "payable" ? <ArrowDownCircle className="w-4 h-4" /> : <ArrowUpCircle className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{bill.description}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" /> {format(parseISO(bill.due_date), "dd/MM/yyyy")}
                        {catName && <Badge variant="outline" className="text-[10px] h-4">{catName}</Badge>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold">{formatCurrency(Number(bill.amount))}</p>
                      <Badge variant={bill.status === "paid" ? "default" : isOverdue ? "destructive" : "secondary"} className="text-[10px]">
                        {bill.status === "paid" ? "Pago" : isOverdue ? "Vencido" : "Pendente"}
                      </Badge>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {bill.status === "pending" && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-500" onClick={() => markBillPaid(bill.id)}><CheckCircle2 className="w-4 h-4" /></Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog("bill", bill)}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteItem("bills", bill.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </motion.div>
                );
              })}
              {bills.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Nenhuma conta cadastrada</p>}
            </div>
          </TabsContent>

          {/* Expenses Tab */}
          <TabsContent value="expenses" className="space-y-4 mt-4">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <h2 className="font-semibold">Lançamentos</h2>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openDialog("category")}><Plus className="w-4 h-4 mr-1" /> Categoria</Button>
                <Button size="sm" onClick={() => openDialog("transaction")}><Plus className="w-4 h-4 mr-1" /> Lançamento</Button>
              </div>
            </div>

            {/* Categories chips */}
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <Badge key={cat.id} variant="outline" className="gap-1 cursor-pointer" onClick={() => openDialog("category", cat)}>
                    <span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
                    {cat.name}
                    <span className="text-[10px] opacity-60">{cat.type === "expense" ? "Desp" : cat.type === "revenue" ? "Rec" : "Ambos"}</span>
                  </Badge>
                ))}
              </div>
            )}

            <div className="space-y-2">
              {periodTransactions.map(tx => {
                const catName = categories.find(c => c.id === tx.category_id)?.name;
                const catColor = categories.find(c => c.id === tx.category_id)?.color || "#6366f1";
                return (
                  <motion.div key={tx.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${tx.type === "expense" ? "bg-red-500/10 text-red-500" : "bg-emerald-500/10 text-emerald-500"}`}>
                      {tx.type === "expense" ? <ArrowDownCircle className="w-4 h-4" /> : <ArrowUpCircle className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{tx.description}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" /> {format(parseISO(tx.date), "dd/MM/yyyy")}
                        {catName && <Badge variant="outline" className="text-[10px] h-4" style={{ borderColor: catColor }}>{catName}</Badge>}
                        {tx.is_recurring && <Badge variant="secondary" className="text-[10px] h-4"><Clock className="w-2.5 h-2.5 mr-0.5" /> Recorrente</Badge>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-sm font-bold ${tx.type === "expense" ? "text-red-500" : "text-emerald-500"}`}>
                        {tx.type === "expense" ? "-" : "+"}{formatCurrency(Number(tx.amount))}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog("transaction", tx)}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteItem("financial_transactions", tx.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </motion.div>
                );
              })}
              {periodTransactions.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Nenhum lançamento no período</p>}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogType === "category" ? (form.editing ? "Editar Categoria" : "Nova Categoria") :
               dialogType === "transaction" ? (form.editing ? "Editar Lançamento" : "Novo Lançamento") :
               form.editing ? "Editar Conta" : "Nova Conta"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {dialogType === "category" ? (
              <>
                <div><Label>Nome</Label><Input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Aluguel" /></div>
                <div><Label>Tipo</Label>
                  <Select value={form.type || "expense"} onValueChange={v => setForm({ ...form, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expense">Despesa</SelectItem>
                      <SelectItem value="revenue">Receita</SelectItem>
                      <SelectItem value="both">Ambos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Cor</Label><Input type="color" value={form.color || "#6366f1"} onChange={e => setForm({ ...form, color: e.target.value })} className="h-10 w-20" /></div>
              </>
            ) : dialogType === "transaction" ? (
              <>
                <div><Label>Tipo</Label>
                  <Select value={form.type || "expense"} onValueChange={v => setForm({ ...form, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expense">Despesa</SelectItem>
                      <SelectItem value="revenue">Receita</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Descrição</Label><Input value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Ex: Conta de luz" /></div>
                <div><Label>Valor (R$)</Label><Input type="number" step="0.01" value={form.amount || ""} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0,00" /></div>
                <div><Label>Data</Label><Input type="date" value={form.date || ""} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
                <div><Label>Categoria</Label>
                  <Select value={form.category_id || ""} onValueChange={v => setForm({ ...form, category_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Sem categoria" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sem categoria</SelectItem>
                      {categories.filter(c => c.type === form.type || c.type === "both").map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={form.is_recurring} onCheckedChange={v => setForm({ ...form, is_recurring: v })} />
                  <Label>Recorrente</Label>
                </div>
                {form.is_recurring && (
                  <div><Label>Intervalo</Label>
                    <Select value={form.recurrence_interval || "monthly"} onValueChange={v => setForm({ ...form, recurrence_interval: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Semanal</SelectItem>
                        <SelectItem value="monthly">Mensal</SelectItem>
                        <SelectItem value="yearly">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>Observações</Label><Textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
              </>
            ) : (
              <>
                <div><Label>Tipo</Label>
                  <Select value={form.type || "payable"} onValueChange={v => setForm({ ...form, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="payable">A Pagar</SelectItem>
                      <SelectItem value="receivable">A Receber</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Descrição</Label><Input value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Ex: Fornecedor XYZ" /></div>
                <div><Label>Valor (R$)</Label><Input type="number" step="0.01" value={form.amount || ""} onChange={e => setForm({ ...form, amount: e.target.value })} /></div>
                <div><Label>Vencimento</Label><Input type="date" value={form.due_date || ""} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
                <div><Label>Categoria</Label>
                  <Select value={form.category_id || ""} onValueChange={v => setForm({ ...form, category_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Sem categoria" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sem categoria</SelectItem>
                      {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.editing && (
                  <div><Label>Status</Label>
                    <Select value={form.status || "pending"} onValueChange={v => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pendente</SelectItem>
                        <SelectItem value="paid">Pago</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>Observações</Label><Textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
              </>
            )}
            <Button className="w-full" onClick={saveItem} disabled={dialogType === "category" ? !form.name : !form.description || !form.amount}>
              {form.editing ? "Salvar Alterações" : "Adicionar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Sub-components
const KpiCard = ({ icon: Icon, label, value, color }: { icon: any; label: string; value: string; color: string }) => (
  <Card>
    <CardContent className="p-3 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-muted ${color}`}><Icon className="w-5 h-5" /></div>
      <div><p className="text-[11px] text-muted-foreground">{label}</p><p className="text-lg font-bold">{value}</p></div>
    </CardContent>
  </Card>
);

const DreRow = ({ label, value, positive, bold, highlight }: { label: string; value: number; positive?: boolean; bold?: boolean; highlight?: boolean }) => (
  <div className={`flex justify-between items-center py-1 ${bold ? "font-semibold" : ""} ${highlight ? "text-lg" : "text-sm"}`}>
    <span className={highlight ? (positive ? "text-emerald-500" : "text-red-500") : ""}>{label}</span>
    <span className={positive ? "text-emerald-500" : value > 0 ? "text-red-500" : "text-muted-foreground"}>
      {formatCurrency(value)}
    </span>
  </div>
);

export default Financeiro;
